import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import Accordion from '../components/Accordion';
import TechDocLayout, { DocSection } from '../components/TechDocLayout';
import { CodePanel, Statement } from '../components/Statement';
import useSeo from '../lib/useSeo';

const ENDPOINTS = [
  { method: 'GET/POST', path: '/api/sources', detail: 'List and add data sources.' },
  { method: 'GET/POST', path: '/api/datasets', detail: 'Add a table. Edit column labels.' },
  { method: 'POST', path: '/api/ingestion/dataset', detail: 'Upload a CSV or Excel file.' },
  { method: 'POST', path: '/api/chat', detail: 'Ask a question.' },
  { method: 'GET', path: '/api/chat/history', detail: 'Get past questions and answers.' },
];

const GETTING_STARTED = [
  { title: 'Connect', detail: 'Add a database or upload a spreadsheet. Passwords are encrypted.' },
  { title: 'Check columns', detail: 'Sidwell labels each column. Fix any mistake.' },
  { title: 'Ask', detail: 'Type a question in plain English.' },
  { title: 'Open the receipt', detail: 'See the exact calculation behind every answer.' },
];

const RESPONSE_FIELDS = [
  { title: 'text', detail: 'The written answer.' },
  { title: 'data', detail: 'The chart and table.' },
  { title: 'plan · pushdown_sql · audit_metadata', detail: 'The plan, the exact query, and the record: rows, time, reference.' },
];

const RESPONSE_JSON = `{
  "text": "Engineering opex exceeded Q2 budget by $420,000 (+14.2%)...",
  "data": { "chart": "bar", "rows": [ /* summary rows only */ ] },
  "plan": { "analysis_type": "variance", "dimension": "department_name" },
  "pushdown_sql": "SELECT d.department_name, SUM(t.actual_amount) - ...",
  "audit_metadata": {
    "trace_id": "tr-8f92-eng-q2",
    "rows_scanned": 18420,
    "rows_egressed": 5,
    "duration_ms": 28
  }
}`;

const SECTIONS = [
  { id: 'in-plain-terms', label: 'In plain terms' },
  { id: 'start-here', label: 'Start here' },
  { id: 'for-developers', label: 'For developers' },
  { id: 'what-you-get-back', label: 'What you get back' },
];

function Documentation() {
  useSeo({
    title: 'Documentation',
    path: '/documentation',
    description: 'How to connect a data source, check the column labels Sidwell assigns, ask questions and verify the answers, plus a guide to the API.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        variant="memo"
        image="facade"
        eyebrow="Documentation"
        title="Connect. Ask. Verify."
        subtitle="A quick guide to getting started."
        badge="API live"
      />

      <TechDocLayout sections={SECTIONS} indexLabel="In this guide">
        <DocSection id="in-plain-terms">
          <Statement title="In plain terms">
            <p className="border-t border-wall/70 px-5 py-7 font-display text-[clamp(24px,2.6vw,38px)] font-light leading-[1.2] sm:px-7">
              Connect, check, ask, verify. Four steps, plus the API.
            </p>
          </Statement>
        </DocSection>

        <DocSection id="start-here" label="Start here" title="Four steps to an answer." intro="The API below is for developers.">
          <ol className="m-0 list-none border-b border-wall/25 p-0">
            {GETTING_STARTED.map((g, i) => (
              <li key={g.title} className="grid grid-cols-[28px_1fr] items-baseline gap-x-3 border-t border-wall/25 py-5 sm:grid-cols-[28px_220px_1fr]">
                <span className="text-[14px] text-glass figure">{i + 1}</span>
                <h3 className="type-h3">{g.title}</h3>
                <p className="col-start-2 mt-1 text-glass sm:col-start-3 sm:mt-0">{g.detail}</p>
              </li>
            ))}
          </ol>
        </DocSection>

        <DocSection id="for-developers" label="For developers" title="The main endpoints." intro="Every request is signed in and limited to your firm.">
          <Statement shadow={false}>
            <div tabIndex={0} role="region" aria-label="API endpoints, scrollable table" className="overflow-x-auto">
              <table className="w-full min-w-[620px] border-collapse text-left text-[15px]">
                <thead>
                  <tr>
                    <th scope="col" className="label px-5 py-3 text-glass sm:pl-7">Method</th>
                    <th scope="col" className="label px-4 py-3 text-glass">Endpoint</th>
                    <th scope="col" className="label py-3 pl-4 pr-5 text-glass sm:pr-7">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ENDPOINTS.map((ep) => (
                    <tr key={ep.path} className="border-t border-stone">
                      <td className="whitespace-nowrap px-5 py-3.5 font-mono text-[13px] text-bronze-deep sm:pl-7">{ep.method}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 font-mono text-[13px]">{ep.path}</td>
                      <td className="py-3.5 pl-4 pr-5 text-glass sm:pr-7">{ep.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Statement>
        </DocSection>

        <DocSection id="what-you-get-back" label="What you get back" title="Every answer, fully visible." intro="One response. Nothing hidden.">
          <div className="border-t border-wall/20">
            {RESPONSE_FIELDS.map((item, i) => (
              <Accordion key={item.title} title={item.title} detail={item.detail} defaultOpen={i === 0} mono />
            ))}
          </div>
          <CodePanel className="mt-8" title="response.json" meta="live" code={RESPONSE_JSON} />
          <p className="mt-5 text-[15px] text-glass">
            More in <Link className="link-inline" to="/innovation">Innovation &amp; Architecture</Link>.
          </p>
        </DocSection>
      </TechDocLayout>

      <CtaSection
        image="boardroom"
        eyebrow="Get Started"
        title="Connect your first source."
        subtitle="Start with sample data, or bring your own."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default Documentation;
